'use client';
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { permisosService, UserPermisos } from '@/lib/ercomex';

interface PermisosContextValue {
  permisos: UserPermisos | null;
  loading: boolean;
  isSuperAdmin: boolean;
  hasPermiso: (key: keyof UserPermisos) => boolean;
  reload: () => void;
}

const PermisosContext = createContext<PermisosContextValue>({
  permisos: null,
  loading: true,
  isSuperAdmin: false,
  hasPermiso: () => true,
  reload: () => {},
});

export const usePermisos = () => useContext(PermisosContext);

const SUPER_ADMIN_EMAILS = ['franco@ercomex.com'];

function checkIsSuperAdmin(email?: string | null, role?: string | null): boolean {
  if (!email && !role) return false;
  if (role === 'super_admin') return true;
  if (email && SUPER_ADMIN_EMAILS.includes(email.toLowerCase())) return true;
  return false;
}

const ALL_PERMISOS_TRUE: Omit<UserPermisos, 'userId' | 'userEmail' | 'id'> = {
  verOperaciones: true, crearOperaciones: true, editarOperaciones: true,
  verClientes: true, crearClientes: true, editarClientes: true,
  verFacturacion: true, crearFacturas: true, editarFacturas: true,
  verCobranzas: true, registrarCobros: true,
  verProveedores: true, cargarFacturasProveedor: true, editarProveedores: true,
  verOrdenesPago: true, crearOrdenesPago: true, aprobarOrdenesPago: true, ejecutarOrdenesPago: true,
  verCaja: true, verEcheqs: true, verPagosMensuales: true, editarPagosMensuales: true,
  verMetricas: true, verAuditoria: true, administrarUsuarios: true,
};

export function PermisosProvider({ children }: { children: React.ReactNode }) {
  const [permisos, setPermisos] = useState<UserPermisos | null>(null);
  const [loading, setLoading] = useState(true);
  const [superAdmin, setSuperAdmin] = useState(false);
  const supabase = createClient();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setPermisos(null);
        setSuperAdmin(false);
        setLoading(false);
        return;
      }

      const role = user.raw_user_meta_data?.role ?? null;
      const isSA = checkIsSuperAdmin(user.email, role);
      setSuperAdmin(isSA);

      if (isSA) {
        setPermisos({
          userId: user.id,
          userEmail: user.email,
          ...ALL_PERMISOS_TRUE,
        });
      } else {
        const p = await permisosService.getByUserId(user.id);
        setPermisos(p);
      }
    } catch {
      setPermisos(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      // TOKEN_REFRESHED fires on every tab focus — ignore it to prevent
      // unnecessary re-renders and scroll position reset
      if (event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') return;
      // SIGNED_IN also fires on tab focus when session is refreshed.
      // If we already have permisos loaded, skip to avoid resetting loading state
      // and losing scroll position.
      if (event === 'SIGNED_IN') {
        setPermisos(prev => {
          if (prev !== null) return prev; // already loaded — do nothing
          // Not loaded yet, trigger load
          load();
          return prev;
        });
        return;
      }
      load();
    });
    return () => subscription.unsubscribe();
  }, [load]);

  const hasPermiso = useCallback((key: keyof UserPermisos): boolean => {
    if (superAdmin) return true;
    if (!permisos) return false;
    const val = permisos[key];
    if (typeof val === 'boolean') return val;
    return false;
  }, [permisos, superAdmin]);

  return (
    <PermisosContext.Provider value={{ permisos, loading, isSuperAdmin: superAdmin, hasPermiso, reload: load }}>
      {children}
    </PermisosContext.Provider>
  );
}
